/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module    public/js/services/emailconnectionservice.js
 * @class     EmailConnectionService
 * @layer     service
 * @summary   Starts the OAuth flow for Gmail / Microsoft 365 for the
 *            authenticated seller.
 *
 * @description
 * Follows the standard TopSail pattern:
 *   1. Await a re-hydrated Firebase Auth session (`waitForLogin()`).
 *   2. Call `generateEmailAuthUrl` via **ApiClient.fetchJson({ auth:true })**
 *      with `?provider=gmail|graph`.
 *   3. Full-page redirect the browser to the provider consent screen.
 *
 * ## Public API
 * – `connectGmail()`   – launch OAuth for Gmail (gmail.send scope)
 * – `connectOutlook()` – launch OAuth for Microsoft Graph (Mail.Send scope)
 *
 * ## Dependencies (DI)
 * • **apiClient** – thin fetch wrapper that handles ID-token injection
 * • **endpoints** – builds environment-aware Cloud-Function URLs
 * • **auth**      – AuthService (waitForLogin → currentUser)
 *
 * @author   TopSail AI
 * @version  1.0.0
 * @since    2025-05-09
 * ─────────────────────────────────────────────────────────────────────────────
 */

/* ───────── Firebase Auth (modular) ───────── */
import {
  getAuth,
  onAuthStateChanged,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-auth.js';

export default class EmailConnectionService {
  constructor({ apiClient, endpoints, auth }) {
    this.api = apiClient;
    this.endpoints = endpoints;
    this.auth = auth;
  }

  /* private helper --------------------------------------------------------- */
  async #connect(provider = 'gmail') {
    /* 1️⃣  ensure Auth is fully ready */
    const user =
      typeof this.auth.waitForLogin === 'function'
        ? await this.auth.waitForLogin(10_000) // preferred helper
        : await new Promise((res, rej) => {
            const u = this.auth.currentUser;
            if (u) return res(u);
            /* fallback on modular Firebase (v9+) if helper missing */
            const authInstance = getAuth(); // singleton per-app
            const unsub = onAuthStateChanged(authInstance, (u2) => {
              if (u2) {
                unsub();
                res(u2);
              }
            });
            setTimeout(() => {
              unsub();
              rej(new Error('auth-timeout'));
            }, 10_000);
          });

    /* 2️⃣  hit Cloud Function – ApiClient injects Bearer for us             */

    const { oauthUrl } = await this.api.fetchJson(
      `/generateEmailAuthUrl?provider=${provider}`, // same-origin rewrite
      { auth: true }
    );

    /* 3️⃣  browser redirect to provider consent page                        */
    window.location = oauthUrl;
  }

  /* public façade ---------------------------------------------------------- */
  connectGmail = () => this.#connect('gmail');
  connectOutlook = () => this.#connect('graph');
}
